// ============================================================================
// FRONT OFFICE SERVICE (E2 Front Office Workspace)
// ============================================================================

import { get, post, patch } from '../client/axiosClient';
import { frontOfficeEndpoints } from '../endpoints/frontOffice.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type { ApiResponse, ID, PaginatedResponse } from '../types';
import type {
  DaySheetSummary,
  FrontOfficeDashboard,
  FrontOfficeQueueItem,
  RoomBoardRoom,
  RoomMoveRequest,
  UpdateRoomStatusRequest,
} from '../types/frontOffice.types';

/** Transforms the backend PagedResult envelope into the frontend PaginatedResponse. */
const toPaginated = (raw: any): PaginatedResponse<FrontOfficeQueueItem> => {
  const pagedResult = raw?.data ?? {};
  return {
    success: raw?.success ?? false,
    message: raw?.message,
    data: pagedResult.items ?? [],
    pagination: {
      currentPage: pagedResult.page ?? 1,
      pageSize: pagedResult.pageSize ?? 20,
      totalPages: pagedResult.totalPages ?? 0,
      totalItems: pagedResult.totalCount ?? 0,
      hasNextPage: pagedResult.hasNext ?? false,
      hasPreviousPage: pagedResult.hasPrevious ?? false,
    },
  };
};

export interface FrontOfficeQueueParams {
  propertyId: string;
  date?: string;
  page?: number;
  pageSize?: number;
}

class FrontOfficeService {
  async getDashboard(propertyId: ID, date?: string): Promise<FrontOfficeDashboard> {
    const endpoint = buildEndpointWithQuery('/front-office/dashboard', { propertyId, date });
    const response = await get<ApiResponse<FrontOfficeDashboard>>(endpoint);
    if (!response.data?.data) throw new Error(response.data?.message || 'Failed to load dashboard.');
    return response.data.data;
  }

  async getArrivals(params: FrontOfficeQueueParams): Promise<PaginatedResponse<FrontOfficeQueueItem>> {
    const endpoint = buildEndpointWithQuery('/front-office/arrivals', params as any);
    const response = await get<any>(endpoint);
    return toPaginated(response.data);
  }

  async getDepartures(params: FrontOfficeQueueParams): Promise<PaginatedResponse<FrontOfficeQueueItem>> {
    const endpoint = buildEndpointWithQuery('/front-office/departures', params as any);
    const response = await get<any>(endpoint);
    return toPaginated(response.data);
  }

  async getInHouse(params: FrontOfficeQueueParams): Promise<PaginatedResponse<FrontOfficeQueueItem>> {
    const endpoint = buildEndpointWithQuery('/front-office/in-house', params as any);
    const response = await get<any>(endpoint);
    return toPaginated(response.data);
  }

  /**
   * Day-sheet search across arrivals, in-house and due-out at once. Ignores the
   * date filter — a guest asking about their bill may have arrived four days
   * ago. Capped server-side; a desk search needing a second page needs a better
   * term instead.
   */
  async searchDaySheet(propertyId: ID, q: string): Promise<FrontOfficeQueueItem[]> {
    const endpoint = buildEndpointWithQuery('/front-office/day-sheet/search', { propertyId, q });
    const response = await get<ApiResponse<FrontOfficeQueueItem[]>>(endpoint);
    return response.data?.data ?? [];
  }

  /**
   * Section totals for the Day Sheet. Also drives the tab badge, which used to
   * sum the dashboard tiles and therefore always reported TODAY regardless of
   * the date toggle — reading both from here means they cannot diverge.
   */
  async getDaySheetSummary(propertyId: ID, date?: string): Promise<DaySheetSummary | null> {
    const endpoint = buildEndpointWithQuery('/front-office/day-sheet/summary', { propertyId, date });
    const response = await get<ApiResponse<DaySheetSummary>>(endpoint);
    return response.data?.data ?? null;
  }

  async getRoomBoard(propertyId: ID, date?: string): Promise<RoomBoardRoom[]> {
    const endpoint = buildEndpointWithQuery('/front-office/room-board', { propertyId, date });
    const response = await get<ApiResponse<RoomBoardRoom[]>>(endpoint);
    return response.data?.data ?? [];
  }

  async updateRoomStatus(roomId: ID, payload: UpdateRoomStatusRequest): Promise<void> {
    const response = await patch<ApiResponse<unknown>>(
      frontOfficeEndpoints.roomStatus(roomId),
      payload,
    );
    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Failed to update room status.');
    }
  }

  async moveRoom(payload: RoomMoveRequest): Promise<void> {
    const response = await post<ApiResponse<unknown>>(frontOfficeEndpoints.roomMove(), payload);
    if (response.data?.success === false) {
      throw new Error(response.data.message || 'Room move failed.');
    }
  }
}

export const frontOfficeService = new FrontOfficeService();
export default frontOfficeService;
